import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  AddFormComponent,
  FormField,
  SectionButton,
} from '@shared/components/add-form/add-form.component';
import {
  AccountFormConfig,
  AccountFormMode,
  getRetryToast,
} from '../../e-invoice-account.config';
import {
  EInvoiceAccount,
  EInvoiceAccountCreateRequest,
  EInvoiceAccountService,
  EInvoiceTemplateItemResponse,
} from '../../services/e-invoice-account.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { EInvoiceProviderService } from '../../../provider/services/e-invoice-provider.service';
import { OrganizationService } from '@app/feature/configuration/user/organizational/services/organization.service';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';

type TemplateLoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

const SETTINGS_SECTION = 'e_invoice.account.form.sections.settings';
const ACCOUNT_SECTION = 'e_invoice.account.form.sections.account';
const TEST_CONNECTION_ACTION = 'test_connection';
const VIEW_TEMPLATE_ACTION = 'view_template';

@Component({
  selector: 'app-account-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    DialogModule,
    ButtonModule,
    AddFormComponent,
    TranslateModule,
  ],
  providers: [MessageService],
  templateUrl: './account-form-wrapper.component.html',
  styleUrls: ['./account-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFormWrapperComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly accountService = inject(EInvoiceAccountService);
  private readonly messageService = inject(MessageService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly translateService = inject(TranslateService);
  private readonly organizationService = inject(OrganizationService);
  private readonly providerService = inject(EInvoiceProviderService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroy$ = new Subject<void>();

  @Input() config!: AccountFormConfig;
  @Input() mode: AccountFormMode = 'add';
  @ViewChild('addForm') addFormComponent?: AddFormComponent;

  get editMode(): boolean {
    return this.mode === 'edit';
  }

  get viewMode(): boolean {
    return this.mode === 'view';
  }

  // Data signals
  loading = signal(false);
  initialLoading = signal(true);
  formFields = signal<FormField[]>([]);
  formData = signal<any>({});
  accountId = signal<number | null>(null);

  // PDF preview state
  pdfDialogVisible = signal(false);
  pdfUrl = signal<SafeResourceUrl | null>(null);
  pdfLoading = signal(false);
  showViewTemplateButton = signal(false);

  // Loaded account (edit/view) and template-load state for the Settings section
  account = signal<EInvoiceAccount | null>(null);
  templateLoadState = signal<TemplateLoadState>('idle');
  templateChanged = signal(false);
  quotaExhausted = computed(
    () => this.account()?.quotaExhausted === true && !this.templateChanged(),
  );
  testingConnection = signal(false);
  sectionButtons = computed<SectionButton[]>(() => {
    const buttons: SectionButton[] = [];
    if (!this.viewMode) {
      buttons.push({
        label: 'e_invoice.account.form.actions.test_connection',
        action: TEST_CONNECTION_ACTION,
        severity: 'secondary',
        outlined: true,
        icon: 'wifi_tethering',
        disabled: this.testingConnection(),
        section: ACCOUNT_SECTION,
      });
    }
    if (this.showViewTemplateButton()) {
      buttons.push({
        label: 'e_invoice.account.form.actions.view_template',
        action: VIEW_TEMPLATE_ACTION,
        severity: 'info',
        outlined: true,
        icon: 'visibility',
        disabled: this.pdfLoading(),
        section: SETTINGS_SECTION,
      });
    }
    return buttons;
  });

  private templateList: EInvoiceTemplateItemResponse[] = [];
  private lastTemplateKey = '';
  private keepSeededSettings = false;
  private lastSelectedTemplate = '';
  private lastCheckedCompanyId = '';
  private templatesLoading = false;
  private updatingFormData = false;
  private currentFormValue: any = {};
  private originalInvoiceSeries = '';
  private pdfBlobUrl: string | null = null;
  private skipCanDeactivate = false;

  ngOnInit(): void {
    this.initializeFormFields();
    this.loadAccountData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.revokePdfUrl();
  }

  private revokePdfUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  private initializeFormFields(): void {
    const fields = withPaginatedFormFields(
      this.config.formFields.map((field) => ({ ...field })),
      {
        providerId: {
          loadPage: (params) =>
            this.providerService.getProviderOptionsPage(params),
          // The saved provider may be off the loaded page or deactivated
          mapCurrentToOption: (value) => ({
            label: this.savedProviderLabel(value),
            value,
          }),
        },
        companyId: {
          loadPage: (params) =>
            this.organizationService.getCompanyOptionsPage(params),
          // The saved company may be off the loaded page or deactivated
          mapCurrentToOption: (value) => ({
            label: this.savedCompanyLabel(value),
            value,
          }),
        },
      },
    );
    this.updateFieldVisibility(fields);
    this.updateFieldReadonly(fields);
    this.formFields.set(fields);
  }

  private loadAccountData(): void {
    if (!this.editMode && !this.viewMode) {
      this.initialLoading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    const accountId = parseInt(id, 10);
    if (isNaN(accountId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    const providerIdParam = this.route.snapshot.queryParamMap.get('providerId');
    const providerId = providerIdParam ? parseInt(providerIdParam, 10) : NaN;
    if (isNaN(providerId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    this.accountId.set(accountId);
    this.loading.set(true);

    this.accountService
      .getAccountById(accountId, providerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (account: EInvoiceAccount) => {
          this.account.set(account);
          const formData = this.transformAccountToFormData(account);
          this.formData.set(formData);
          this.currentFormValue = formData;
          this.originalInvoiceSeries = formData.invoiceSeries ?? '';
          if (formData.invoiceTemplate) {
            this.showViewTemplateButton.set(true);
          }
          const fields = this.seedSavedSettingsOptions(
            [...this.formFields()],
            account,
            formData,
          );
          this.updateFieldVisibility(fields);
          this.updateFieldReadonly(fields);
          this.formFields.set(fields);
          this.loading.set(false);
          this.initialLoading.set(false);
          this.showQuotaExhaustedToast(account);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.account.messages.error.failed_to_load_account',
            ),
          });
          this.router.navigate([this.config.listRoute]);
        },
      });
  }

  private showQuotaExhaustedToast(account: EInvoiceAccount): void {
    if (!this.quotaExhausted()) return;
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        'e_invoice.account.banner.exhausted_title',
      ),
      detail: this.translateService.instant(
        'e_invoice.account.banner.exhausted_message',
        {
          template: account.invoiceTemplate,
          series: account.invoiceSeries,
          pending: account.pendingFailedCount ?? 0,
        },
      ),
    });
  }

  /**
   * Shows the saved values as the only options until the async option lists
   * arrive, so the dropdowns don't render blank and then jump to the real options.
   */
  private seedSavedSettingsOptions(
    fields: FormField[],
    account: EInvoiceAccount,
    formData: Record<string, string>,
  ): FormField[] {
    const seeded: Record<string, { label?: string; value: unknown }> = {
      providerId: { label: account.providerName, value: account.providerId },
      companyId: { label: account.companyName, value: formData['companyId'] },
      invoiceTemplate: {
        label: formData['invoiceTemplate'],
        value: formData['invoiceTemplate'],
      },
      invoiceSeries: {
        label: formData['invoiceSeries'],
        value: formData['invoiceSeries'],
      },
    };
    return fields.map((field) => {
      const option = seeded[field.controlName];
      if (!option?.label || option.value == null || option.value === '') {
        return field;
      }
      const isSettingsField =
        field.controlName === 'invoiceTemplate' ||
        field.controlName === 'invoiceSeries';
      if (isSettingsField && !this.viewMode) {
        this.keepSeededSettings = true;
      }
      return {
        ...field,
        // In edit mode they stay disabled (showing the saved values) until the
        // password is re-entered and the templates load with it
        ...(isSettingsField && this.viewMode && { disabled: false }),
        options: [{ label: option.label, value: option.value }],
      } as FormField;
    });
  }

  private transformAccountToFormData(account: EInvoiceAccount): any {
    return {
      providerId: account.providerId,
      companyId: account.companyId != null ? String(account.companyId) : null,
      username: account.username ?? '',
      password: account.password ?? '',
      invoiceTemplate: account.invoiceTemplate ?? '',
      invoiceSeries: account.invoiceSeries ?? '',
      active: account.status,
    };
  }

  private updateFieldVisibility(fields: FormField[]): void {
    fields.forEach((field) => {
      if (field.controlName === 'password') {
        field.hidden = this.viewMode;
        if (this.viewMode) {
          field.validators = [];
          field.required = false;
        }
      }
    });
  }

  private updateFieldReadonly(fields: FormField[]): void {
    if (this.editMode) {
      fields.forEach((field) => {
        if (
          field.controlName === 'providerId' ||
          field.controlName === 'taxCode'
        ) {
          field.readonly = true;
        }
        if (field.controlName === 'password') {
          field.required = false;
          field.validators = [];
          field.placeholder =
            'e_invoice.account.form.placeholders.enter_password_keep';
        }
      });
    }
    if (this.viewMode) {
      fields.forEach((field) => {
        if (field.controlName === 'invoiceTemplate') {
          field.type = 'text';
          field.readonly = true;
          field.disabled = false;
        }
        if (field.controlName === 'invoiceSeries') {
          field.type = 'text';
          field.readonly = true;
          field.disabled = false;
        }
      });
    }
  }

  onFormValueChanges(formValue: any): void {
    if (this.viewMode || this.updatingFormData) return;

    const companyId = formValue?.companyId;

    this.currentFormValue = formValue;
    this.validateCompanyUnique(companyId, formValue?.active);

    const providerId = formValue?.providerId;
    const username = formValue?.username?.trim();
    const password = formValue?.password?.trim();

    if (providerId && username && password) {
      const templateKey = `${providerId}|${username}|${password}`;
      // Silent load: toasts are reserved for an explicit "Test Connection"
      if (templateKey !== this.lastTemplateKey && !this.templatesLoading) {
        this.lastTemplateKey = templateKey;
        this.lastSelectedTemplate = '';
        this.keepSeededSettings = false;
        this.loadTemplates(this.providerCodeFor(providerId), username, password);
      }
    } else if (this.keepSeededSettings) {
      // The saved password isn't returned, so templates can't load yet; leave the
      // seeded saved values as they are instead of disabling and clearing them.
      this.templateChanged.set(false);
      return;
    } else {
      this.lastTemplateKey = '';
      this.lastSelectedTemplate = '';
      this.templateList = [];
      this.templateLoadState.set('idle');
      this.updateSettingsFieldsState(true, []);
    }

    // Handle template selection → filter invoiceSeries options
    const selectedTemplate = formValue?.invoiceTemplate ?? '';
    this.templateChanged.set(
      !!selectedTemplate &&
        selectedTemplate !== (this.account()?.invoiceTemplate ?? ''),
    );
    if (selectedTemplate &&selectedTemplate !== this.lastSelectedTemplate) {
      this.lastSelectedTemplate = selectedTemplate;
      const matchedSeries = this.seriesOptionsFor(selectedTemplate);
      this.updateInvoiceSeriesOptions(matchedSeries);
      const firstSeries =
        matchedSeries.length > 0 ? matchedSeries[0].value : '';
      this.updatingFormData = true;
      this.patchFormData({
        invoiceTemplate: selectedTemplate,
        invoiceSeries: firstSeries,
      });
      this.showViewTemplateButton.set(!!firstSeries);
      this.cdr.markForCheck();
      setTimeout(() => (this.updatingFormData = false), 0);
    } else if (!selectedTemplate) {
      this.lastSelectedTemplate = '';
      this.updateInvoiceSeriesOptions([]);
      this.showViewTemplateButton.set(false);
    }
  }

  /**
   * Flags the company control when the account is active and the selected company
   * already has another active account. Inactive accounts may share a company.
   */
  private validateCompanyUnique(companyId: unknown, active: unknown): void {
    const key = companyId && active ? String(companyId) : '';
    if (key === this.lastCheckedCompanyId) return;
    this.lastCheckedCompanyId = key;
    if (!key) {
      this.clearCompanyDuplicateError();
      return;
    }

    this.accountService
      .hasActiveAccountForCompany(Number(key), this.accountId() ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (duplicate) => {
          const control = this.addFormComponent?.form.get('companyId');
          // Ignore a stale answer once the user has picked another company
          if (!duplicate || !control || key !== this.lastCheckedCompanyId) {
            return;
          }
          control.setErrors({ ...control.errors, companyHasAccount: true });
          control.markAsTouched();
          this.cdr.markForCheck();
        },
      });
  }

  private clearCompanyDuplicateError(): void {
    const control = this.addFormComponent?.form.get('companyId');
    if (!control?.hasError('companyHasAccount')) return;
    const { companyHasAccount, ...rest } = control.errors ?? {};
    control.setErrors(Object.keys(rest).length ? rest : null);
    this.cdr.markForCheck();
  }

  private loadTemplates(
    providerCode: string,
    username: string,
    password: string,
    notify = false,
  ): void {
    this.templatesLoading = true;
    this.testingConnection.set(true);
    this.templateLoadState.set('loading');

    // A saved template is already shown, so skip the spinner that would resize the input
    if (!this.currentFormValue?.invoiceTemplate) {
      this.updateTemplateFieldLoading(true);
    }

    this.accountService
      .getTemplates({
        invoiceType: '',
        username,
        password,
        providerCode,
        accountId: this.accountId() ?? undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.templateList = templates;
          const uniqueCodes = [
            ...new Set(templates.map((t) => t.templateCode)),
          ];
          const options = uniqueCodes.map((code) => ({
            label: this.templateOptionLabel(code),
            value: code,
          }));
          // In edit mode, if a template is already selected, populate invoice series
          const currentTemplate =
            this.currentFormValue?.invoiceTemplate ||
            this.formData()?.invoiceTemplate;
          const matchedSeries = currentTemplate
            ? this.seriesOptionsFor(currentTemplate)
            : [];
          // One fields update so the inputs re-render once instead of per step
          this.updateSettingsFieldsState(false, options, matchedSeries);
          this.templatesLoading = false;
          this.testingConnection.set(false);
          this.templateLoadState.set(templates.length ? 'ready' : 'empty');
          if (!templates.length) {
            if (notify) this.showTemplateStateToast('warn', 'empty');
          } else if (notify) {
            this.showConnectionToast('success', 'success');
          }
          this.reloadTemplatesIfCredentialsChanged();

          if (currentTemplate) {
            this.showViewTemplateButton.set(matchedSeries.length > 0);

            const savedSeries = this.originalInvoiceSeries;
            const isValid =
              !!savedSeries &&
              matchedSeries.some((s) => s.value === savedSeries);
            const alreadyShown =
              this.currentFormValue?.invoiceSeries === savedSeries;
            if (isValid && !alreadyShown) {
              setTimeout(() => {
                this.updatingFormData = true;
                this.patchFormData({ invoiceSeries: savedSeries });
                this.cdr.markForCheck();
                setTimeout(() => (this.updatingFormData = false), 0);
              });
            }
          }
        },
        error: () => {
          this.templateList = [];
          this.updateSettingsFieldsState(false, []);
          this.templatesLoading = false;
          this.testingConnection.set(false);
          this.templateLoadState.set('error');
          this.updateTemplateFieldLoading(false);
          if (notify) this.showTemplateStateToast('error', 'error');
        },
      });
  }

  private showTemplateStateToast(
    severity: 'warn' | 'error',
    key: 'empty' | 'error',
  ): void {
    this.messageService.add({
      severity,
      summary: this.translateService.instant(
        severity === 'error'
          ? 'common.messages.error'
          : 'common.messages.warning',
      ),
      detail: this.translateService.instant(
        `e_invoice.account.form.template_state.${key}`,
      ),
    });
    this.cdr.markForCheck();
  }

  /**
   * Overlays the live form values on formData so that re-pushing initialData into
   * add-form never reverts what the user typed (e.g. password) to the loaded account.
   */
  private patchFormData(partial: Record<string, unknown>): void {
    this.formData.set({
      ...this.formData(),
      ...this.currentFormValue,
      ...partial,
    });
  }

  /** Credentials typed while a load was in flight are skipped; pick them up once it ends. */
  private reloadTemplatesIfCredentialsChanged(): void {
    const { providerId, username, password } = this.currentFormValue ?? {};
    const user = username?.trim();
    const pass = password?.trim();
    if (!providerId || !user || !pass) return;
    const key = `${providerId}|${user}|${pass}`;
    if (key === this.lastTemplateKey) return;
    this.lastTemplateKey = key;
    this.lastSelectedTemplate = '';
    this.loadTemplates(this.providerCodeFor(providerId), user, pass);
  }

  /** The provider cache only fills once the provider list loads, which view mode never does. */
  private providerCodeFor(providerId: number): string {
    return (
      this.providerService.getProviderCodeById(providerId) ??
      this.account()?.providerCode ??
      ''
    );
  }

  retryLoadTemplates(): void {
    const formValue = this.currentFormValue;
    const providerId = formValue?.providerId;
    const username = formValue?.username?.trim();
    const password = formValue?.password?.trim();
    if (!providerId || !username || !password || this.templatesLoading) return;
    this.loadTemplates(
      this.providerCodeFor(providerId),
      username,
      password,
    );
  }

  private templateOptionLabel(templateCode: string): string {
    const anyExhausted = this.templateList.some(
      (t) => t.templateCode === templateCode && t.quotaExhausted,
    );
    return anyExhausted
      ? this.translateService.instant(
          'e_invoice.account.form.options.template_exhausted',
          { template: templateCode },
        )
      : templateCode;
  }

  private seriesOptionsFor(
    templateCode: string,
  ): { label: string; value: string }[] {
    return this.templateList
      .filter((t) => t.templateCode === templateCode)
      .map((t) => ({
        label: this.translateService.instant(
          t.quotaExhausted
            ? 'e_invoice.account.form.options.series_exhausted'
            : 'e_invoice.account.form.options.series_usage',
          { series: t.invoiceSeri, count: t.issuedCount ?? 0 },
        ),
        value: t.invoiceSeri,
      }));
  }

  onSectionAction(event: { section: string; action: string }): void {
    if (event.action === VIEW_TEMPLATE_ACTION) {
      this.viewTemplatePreview();
    } else if (event.action === TEST_CONNECTION_ACTION) {
      this.testConnection();
    }
  }

  /** Authenticates against the provider with the typed credentials and reports the result. */
  testConnection(): void {
    const { providerId, username, password } = this.currentFormValue ?? {};
    const user = username?.trim();
    const pass = password?.trim();
    if (!providerId || !user || !pass) {
      this.showConnectionToast('warn', 'credentials_required');
      return;
    }
    if (this.templatesLoading) return;
    this.lastTemplateKey = `${providerId}|${user}|${pass}`;
    this.keepSeededSettings = false;
    this.lastSelectedTemplate = '';
    this.loadTemplates(this.providerCodeFor(providerId), user, pass, true);
  }

  private showConnectionToast(
    severity: 'success' | 'warn' | 'error',
    key: string,
  ): void {
    const summaryKeys = {
      success: 'common.messages.success',
      warn: 'common.messages.warning',
      error: 'common.messages.error',
    };
    this.messageService.add({
      severity,
      summary: this.translateService.instant(summaryKeys[severity]),
      detail: this.translateService.instant(
        `e_invoice.account.messages.connection.${key}`,
      ),
    });
    this.cdr.markForCheck();
  }

  private updateSettingsFieldsState(
    disabled: boolean,
    templateOptions: { label: string; value: string }[],
    seriesOptions: { label: string; value: string }[] = [],
  ): void {
    const fields = this.formFields().map((f) => {
      if (f.controlName === 'invoiceTemplate') {
        return { ...f, disabled, loading: false, options: templateOptions };
      }
      if (f.controlName === 'invoiceSeries') {
        return {
          ...f,
          disabled: disabled || seriesOptions.length === 0,
          options: seriesOptions,
        };
      }
      return f;
    });
    this.formFields.set(fields);
    this.cdr.markForCheck();
  }

  private updateInvoiceSeriesOptions(
    options: { label: string; value: string }[],
  ): void {
    const fields = this.formFields().map((f) => {
      if (f.controlName === 'invoiceSeries') {
        return { ...f, options, disabled: options.length === 0 };
      }
      return f;
    });
    this.formFields.set(fields);
    this.cdr.markForCheck();
  }

  private updateTemplateFieldLoading(isLoading: boolean): void {
    const fields = this.formFields().map((f) => {
      if (f.controlName === 'invoiceTemplate') {
        return { ...f, loading: isLoading };
      }
      return f;
    });
    this.formFields.set(fields);
    this.cdr.markForCheck();
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.router.navigate([this.config.listRoute]);
      return;
    }

    if (event.action === 'save') {
      this.handleSave(event.data);
      return;
    }
  }

  viewTemplatePreview(): void {
    const formValue = this.currentFormValue;
    const providerId = formValue?.providerId;
    const providerCode = this.providerCodeFor(providerId);
    const templateCode = formValue?.invoiceTemplate;
    const invoiceSeries = formValue?.invoiceSeries;
    const username = formValue?.username?.trim();
    const password = formValue?.password?.trim();

    // A saved account's password isn't returned, so the backend uses its stored credentials
    const savedAccountId = !password ? this.accountId() : null;

    if (!templateCode || !providerCode || !username) return;
    if (!password && savedAccountId == null) return;

    this.pdfLoading.set(true);
    this.pdfDialogVisible.set(true);
    this.revokePdfUrl();

    this.accountService
      .getTemplatePreview({
        templateCode,
        invoiceSeries: invoiceSeries ?? '',
        username,
        password: password ?? '',
        providerCode,
        accountId: savedAccountId ?? undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.pdfBlobUrl = URL.createObjectURL(blob);
          this.pdfUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl),
          );
          this.pdfLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.pdfLoading.set(false);
          this.pdfDialogVisible.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.account.messages.error.failed_to_load_preview',
            ),
          });
          this.cdr.markForCheck();
        },
      });
  }

  closePdfDialog(): void {
    this.pdfDialogVisible.set(false);
    this.revokePdfUrl();
    this.pdfUrl.set(null);
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private handleSave(formData: any): void {
    if (!formData) return;

    const payload = this.transformFormDataToPayload(formData);
    this.loading.set(true);

    if (this.editMode && this.accountId()) {
      this.accountService
        .updateAccount(this.accountId()!, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'e_invoice.account.messages.success.updated',
              ),
            });
            this.router.navigate([this.config.listRoute]);
          },
          error: (error) => {
            this.loading.set(false);
            this.showSaveError(
              error,
              'e_invoice.account.messages.error.failed_to_update',
            );
          },
        });
    } else {
      this.accountService
        .createAccount(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'e_invoice.account.messages.success.created',
              ),
            });
            this.router.navigate([this.config.listRoute]);
          },
          error: (error) => {
            this.loading.set(false);
            this.showSaveError(
              error,
              'e_invoice.account.messages.error.failed_to_create',
            );
          },
        });
    }
  }

  private showSaveError(error: any, fallbackKey: string): void {
    const codeKey = this.accountService.getErrorTranslationKey(error);
    this.errorDialogService.showError(
      codeKey
        ? this.translateService.instant(codeKey, this.accountService.getErrorParams(error))
        : this.errorTranslatorService.translate(
            error.error?.message,
            fallbackKey,
          ),
    );
  }

  retryPendingOrders(): void {
    const account = this.account();
    if (!account) return;
    this.loading.set(true);
    this.accountService
      .retryPendingOrders(account.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.loading.set(false);
          this.account.set({
            ...account,
            pendingFailedCount: result.remainingPending ?? 0,
            // An already-running replay reports no verdict, so keep the banner as is
            quotaExhausted: result.alreadyRunning
              ? account.quotaExhausted
              : result.quotaExhausted === true,
          });
          const toast = getRetryToast(result);
          this.messageService.add({
            severity: toast.severity,
            summary: this.translateService.instant(toast.summaryKey),
            detail: this.translateService.instant(toast.detailKey, {
              resolved: result.resolved ?? 0,
              remaining: result.remainingPending ?? 0,
            }),
          });
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.account.messages.error.failed_to_retry',
            ),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private transformFormDataToPayload(
    formData: any,
  ): EInvoiceAccountCreateRequest {
    return {
      providerId: formData.providerId,
      userName: formData.username?.trim(),
      // Omitted when blank so the backend keeps the stored password
      password: formData.password?.trim() || undefined,
      invoiceTemplate: formData.invoiceTemplate?.trim(),
      invoiceSeries: formData.invoiceSeries?.trim(),
      companyId: formData.companyId ? Number(formData.companyId) : undefined,
      companyName: this.companyNameFor(formData.companyId),
      status: formData.active,
    };
  }

  /** The saved name while the company is unchanged, otherwise the selected option's label. */
  private companyNameFor(companyId: unknown): string | undefined {
    if (!companyId) return undefined;
    const saved = this.account();
    if (saved?.companyId != null && String(saved.companyId) === String(companyId)) {
      return saved.companyName?.trim() || undefined;
    }
    const field = this.formFields().find((f) => f.controlName === 'companyId');
    const options = field
      ? this.addFormComponent?.enrichPaginatedField(field).options
      : undefined;
    const match = options?.find((o) => String(o.value) === String(companyId));
    return match?.label?.trim() || undefined;
  }

  private savedProviderLabel(value: unknown): string {
    const saved = this.account();
    const isSaved =
      saved?.providerId != null && String(saved.providerId) === String(value);
    return (isSaved && saved?.providerName) || String(value);
  }

  private savedCompanyLabel(value: unknown): string {
    const saved = this.account();
    const isSaved =
      saved?.companyId != null && String(saved.companyId) === String(value);
    return (isSaved && saved?.companyName) || String(value);
  }
}
